import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ConnectionError from "../../components/ConnectionError/ConnectionError";
import useReveal from "../../hooks/useReveal";
import { publicContentService } from "../../services/publicContentService";
import type { TeamMemberRecord } from "../../types";
import "./About.css";

const missionImages: string[] = [
  "/images/mission-1.jpg",
  "/images/mission-2.jpg",
  "/images/mission-3.jpg",
  "/images/mission-4.jpg",
];

function About() {
  const { t, i18n } = useTranslation();
  const missionItems = t("about.missionItems", {
    returnObjects: true,
  }) as string[];
  const [team, setTeam] = useState<TeamMemberRecord[]>([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState(false);

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  const loadTeam = useCallback(async () => {
    setTeamLoading(true);
    setTeamError(false);
    try {
      const data = await publicContentService.getTeam();
      setTeam(data);
    } catch {
      setTeamError(true);
    } finally {
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  useReveal([team.length, teamLoading]);

  return (
    <main className="page-main">
      {/* About intro */}
      <section className="section about-intro-section">
        <div className="container">
          <div className="about-intro-text reveal">
            <h1 className="section-title">{t("about.title")}</h1>
            <p>{t("about.p1")}</p>
            <p>{t("about.p2")}</p>
            <p>{t("about.p3")}</p>
          </div>
        </div>
        <div
          className="about-intro-img reveal reveal-delay-2"
          style={{ backgroundImage: "url(/images/about-intro.jpg)" }}
        ></div>
      </section>

      {/* Mission */}
      <section className="section about-mission-section">
        <div className="container">
          <h2 className="section-title section-title--center reveal">
            {t("about.mission")}
          </h2>
        </div>
        <div className="mission-checker">
          <div className="mission-row reveal">
            <div className="mission-cell mission-cell-img">
              <img src={missionImages[0]} alt="" />
            </div>
            <div className="mission-cell mission-cell-text bg-brand">
              <p>{missionItems[0]}</p>
            </div>
            <div className="mission-cell mission-cell-img">
              <img src={missionImages[1]} alt="" />
            </div>
            <div className="mission-cell mission-cell-text bg-brand">
              <p>{missionItems[1]}</p>
            </div>
          </div>
          <div className="mission-row reveal reveal-delay-2">
            <div className="mission-cell mission-cell-text bg-dark">
              <p>{missionItems[2]}</p>
            </div>
            <div className="mission-cell mission-cell-img">
              <img src={missionImages[2]} alt="" />
            </div>
            <div className="mission-cell mission-cell-text bg-dark">
              <p>{missionItems[3]}</p>
            </div>
            <div className="mission-cell mission-cell-img">
              <img src={missionImages[3]} alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section about-team-section">
        <div className="container">
          <h2 className="section-title section-title--center reveal">
            {t("about.team")}
          </h2>
          {teamLoading ? (
            <div className="admin-loading">
              <span className="auth-spinner auth-spinner-brand"></span>
            </div>
          ) : teamError ? (
            <ConnectionError onRetry={loadTeam} />
          ) : (
            <>
              <div className="team-grid team-grid--leadership">
                {team.slice(0, 3).map((member, i) => {
                  const name = isArabic ? member.nameAr : member.nameEn;
                  return (
                    <div
                      key={member.id}
                      className={`team-member reveal reveal-delay-${(i % 4) + 1}`}
                    >
                      <div className="team-photo">
                        {member.imageUrl && (
                          <img src={member.imageUrl} alt={name} />
                        )}
                      </div>
                      <h4>{name}</h4>
                      <p>{isArabic ? member.titleAr : member.titleEn}</p>
                    </div>
                  );
                })}
              </div>
              <div className="team-grid">
                {team.slice(3).map((member, i) => {
                  const name = isArabic ? member.nameAr : member.nameEn;
                  return (
                    <div
                      key={member.id}
                      className={`team-member reveal reveal-delay-${(i % 4) + 1}`}
                    >
                      <div className="team-photo">
                        {member.imageUrl && (
                          <img src={member.imageUrl} alt={name} />
                        )}
                      </div>
                      <h4>{name}</h4>
                      <p>{isArabic ? member.titleAr : member.titleEn}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

export default About;
